import { Children, FC, ReactNode } from 'react'
import { trackingClient } from '../clients'
import { ListingSectionObject } from '../transports/common/listing-section-transport'
import { getSearchDate } from '../utils/miscellaneous-utils'
import { CookieManagerService } from '../services'

const LISTING_VIEW_EVENT_ID = 10
const LISTING_CLICK_EVENT_ID = 1

const TOP_OFFER_VIEW_EVENT_ID = 15
const TOP_OFFER_CLICK_EVENT_ID = 16

const TOP_LISTING_VIEW_EVENT_ID = 13
const TOP_LISTING_CLICK_EVENT_ID = 14

const PREMIUM_TOP_LISTING_VIEW_EVENT_ID = 19
const PREMIUM_TOP_LISTING_CLICK_EVENT_ID = 20

const RESULT_LISTING_VIEW_EVENT_ID = 21

const RLP_POPULAR_FILTER = 'region_landingpage_popular_filter'
const RLP_TOP_FILTER = 'region_landingpage_top_rating'

export const SRL_PAGE_SIZE = 25
let componentName = 'unknown'

export const getContent = (children: any, element: FC<{}>): ReactNode | null => {
    let content = null
    if (children) {
        Children.forEach(children, child => {
            if (child && child.type === element) {
                content = child && child.props && child.props.children
                return content
            }
        })
    }
    return content
}

export const getPropertyClickRedirectUrl = (baseUrl: string, href: string) => {
    try {
        return new URL(href)
    } catch {
        return new URL(href, baseUrl)
    }
}

export const preventDefaultHandler = (e: Event) => {
    e.preventDefault()
}

export const verifyEmail = (email: string) => {
    const mailFormat = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/
    return mailFormat.test(email)
}

export const trackListings = (body: Record<string, any>) => {
    trackingClient.trackRegionLandingPageListings(body).then()
}

export const getMimeTypeFromUrl = (url?: string): string => {
    if (!url) {
        return 'image/jpeg'
    } // default fallback

    const extension = url.split('.').pop()?.toLowerCase().split('?')[0] // handle query params too

    switch (extension) {
        case 'jpg':
        case 'jpeg': {
            return 'image/jpeg'
        }
        case 'png': {
            return 'image/png'
        }
        case 'webp': {
            return 'image/webp'
        }
        case 'gif': {
            return 'image/gif'
        }
        case 'avif': {
            return 'image/avif'
        }
        case 'svg': {
            return 'image/svg+xml'
        }
        default: {
            return 'image/jpeg'
        } // default fallback if unknown
    }
}

export const getTrackingArray = (
    viewTracking: boolean,
    propertyObject: ListingSectionObject,
    listingType?: string | null,
    index?: number,
) => {
    const trackingArray = []
    const date = new Date()
    const dateString = getSearchDate(date)
    const timeString = date.toLocaleTimeString('en-US', { hour12: false })
    const _url = globalThis.location.href
    const _timestamp = `${dateString} ${timeString}`
    const _eventValue = Number(propertyObject.id)

    const _resultListingViewEventData = {
        url: _url,
        filters: {
            region_title: propertyObject?.seoRegion?.name ?? '',
            region_id: propertyObject?.regionId ?? '',
            adult: 0,
            arrival_date: null,
            children: 0,
            departure_date: null,
        },
        listing: {
            tfw_object_id: propertyObject.id,
            provider_id: propertyObject.providerId,
            is_premium_top_listing: !!propertyObject.isPremiumTopObject,
            availability: propertyObject.availabilityState ? propertyObject.availabilityState : 'on_request',
            external_object_id: propertyObject.externalObjectId,
            features: propertyObject.features ? Object.keys(propertyObject.features) : [],
            index: index ?? 1,
            is_combo_listing: propertyObject.isComboListing,
            page: index ? Math.ceil(index / SRL_PAGE_SIZE) : 1,
            price: propertyObject.price,
            component: {
                name: 'unknown',
            },
        },
        google_analytics: [
            {
                key: '_gid',
                value: CookieManagerService.getCookie('_gid'),
            },
            {
                key: '_ga',
                value: CookieManagerService.getCookie('_ga'),
            },
        ],
        timestamp: _timestamp,
    }

    if (
        propertyObject.isTopObject &&
        listingType !== 'RED' &&
        listingType !== 'TOP_RATED' &&
        listingType !== 'POPULAR_FILTER_BASED'
    ) {
        viewTracking
            ? trackingArray.push({ eventType: TOP_LISTING_VIEW_EVENT_ID, eventValue: _eventValue })
            : trackingArray.push({ eventType: TOP_LISTING_CLICK_EVENT_ID, eventValue: _eventValue })
        return trackingArray
    } else if (listingType === 'RED') {
        viewTracking
            ? trackingArray.push({ eventType: TOP_OFFER_VIEW_EVENT_ID, eventValue: _eventValue })
            : trackingArray.push({ eventType: TOP_OFFER_CLICK_EVENT_ID, eventValue: _eventValue })
        return trackingArray
    } else if (propertyObject.isPremiumTopObject) {
        viewTracking
            ? trackingArray.push({
                  eventType: PREMIUM_TOP_LISTING_VIEW_EVENT_ID,
                  eventValue: _eventValue,
              })
            : trackingArray.push({
                  eventType: PREMIUM_TOP_LISTING_CLICK_EVENT_ID,
                  eventValue: _eventValue,
              })
    } else {
        /*https://oyorooms.atlassian.net/browse/TRAUM-6507*/
        if (listingType === 'POPULAR_FILTER_BASED') {
            _resultListingViewEventData.listing.component.name = RLP_POPULAR_FILTER
            componentName = RLP_POPULAR_FILTER
        } else if (listingType === 'TOP_RATED') {
            _resultListingViewEventData.listing.component.name = RLP_TOP_FILTER
            componentName = RLP_TOP_FILTER
        }

        viewTracking
            ? trackingArray.push(
                  { eventType: LISTING_VIEW_EVENT_ID, eventValue: _eventValue },
                  {
                      eventType: RESULT_LISTING_VIEW_EVENT_ID,
                      eventValue: _eventValue,
                      eventData: _resultListingViewEventData,
                  },
              )
            : trackingArray.push({ eventType: LISTING_CLICK_EVENT_ID, eventValue: _eventValue })

        if (!viewTracking) {
            CookieManagerService.setCookie('srlObjectClicked_' + propertyObject.id, globalThis.location.href, {
                expires: 7,
            })
            CookieManagerService.setCookie('srlObjectClicked_' + propertyObject.id + '_componentName', componentName, {
                expires: 7,
            })
        }
    }

    return trackingArray
}

/**
 * Calculates scroll offsets for carousel dots.
 * Keeps the first 3 and last 2 dots fixed to create a controlled scrolling effect.
 */
export const getDotOffsets = (numberOfDots: number) => {
    const dotOffsets = []
    let offset = 0
    for (let dotIndex = 0; dotIndex < numberOfDots; dotIndex++) {
        // Increment offset by 10 only if the dot is NOT among the first 3 or the last 2
        offset = dotIndex < 3 || dotIndex > numberOfDots - 3 ? offset : (offset += 10)
        dotOffsets.push(offset)
    }
    return dotOffsets
}
